import { Backdrop, Box, CircularProgress } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import clsx from "clsx";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import ".\\loadingProvider.css";
const LoadingContext = createContext({
  removeLoading: () => ({}),
  addLoading: () => ({}),
  isLoading: () => ({}),
});

export const useLoading = () => useContext(LoadingContext);

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  logo: {
    height: 100,
    width: 100,
    backgroundColor: "#fff",
    borderRadius: "50%",
    padding: 16,
    boxShadow:
      "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
  },
  message: {
    fontSize: 16,
  },
  logoAnimation: {
    animationTimingFunction: "linear",
    animation: "heartbeat 3s infinite",
  },
  progress: {
    zIndex: 0,
  },
}));
let loadingIds = [];
let messages = [];
const getLoadingIndex = (uid) => loadingIds.indexOf(uid);
const createGuid = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
export function LoadingProvider({ children }) {
  const classes = useStyles();
  const [loadingsCount, setLoadingsCount] = useState(0);
  const [peak, setPeak] = useState(0);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState(0);

  const capitalFirstLetter = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  const capitalSentence = useCallback(
    (sentence) =>
      sentence.toLowerCase().split(" ").map(capitalFirstLetter).join(" "),
    []
  );

  const addLoadingIdWithCollitionChecking = useCallback(() => {
    while (true) {
      var uid = createGuid();
      const index = getLoadingIndex(uid);
      if (index === -1) {
        loadingIds.push(uid);
        return uid;
      }
    }
  }, []);

  const addLoading = useCallback(
    (rawMessage) => {
      if (!rawMessage) throw new Error("loading message is mandatory");
      const message = capitalSentence(rawMessage);
      setPeak((prev) => prev + 1);
      const uuid = addLoadingIdWithCollitionChecking();
      setLoadingsCount(loadingIds.length);
      messages.push({ id: uuid, message });
      setMessage(message);
      return uuid;
    },
    [addLoadingIdWithCollitionChecking, capitalSentence]
  );

  const removeLoading = useCallback((uid) => {
    if (!uid) throw new Error("uuid is mandatory");
    const index = getLoadingIndex(uid);
    loadingIds.splice(index, 1);
    setLoadingsCount(loadingIds.length);
    messages = messages.filter((m) => m.id !== uid);
    setMessage(messages[messages.length - 1]?.message);
    if (loadingIds.length === 0) {
      setPeak(0);
      setMessage(null);
    }
  }, []);

  const isLoading = useCallback(() => loadingIds.length > 0, []);

  const functions = useMemo(
    () => ({ addLoading, removeLoading, isLoading }),
    [addLoading, isLoading, removeLoading]
  );

  useEffect(() => {
    if (peak === 0) {
      setProgress(0);
      return;
    }
    const part = 100 / peak;
    const progress = 100 - (loadingsCount * 100) / peak;
    setProgress(progress + part);
  }, [loadingsCount, peak]);

  return (
    <LoadingContext.Provider value={functions}>
      <Backdrop className={classes.backdrop} open={peak > 0}>
        <Box position="relative" display="inline-flex">
          <CircularProgress variant="determinate" value={progress} size={110} />
          <Box
            top={0}
            left={0}
            bottom={0}
            right={0}
            position="absolute"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <img
              src="/logo.svg"
              alt="logo"
              className={`${classes.logo} ${classes.logoAnimation}`}
            />
          </Box>
        </Box>
        <Box
          className={clsx(classes.message)}
          style={{ color: !Boolean(message) ? "transparent" : null }}
        >
          {message || "placeholder"}
        </Box>
      </Backdrop>
      {children}
    </LoadingContext.Provider>
  );
}
